/** Bank statement PDF upload + parsed credit/debit transaction explorer. */
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, UploadCloud, Loader2, Trash2, Landmark, Search, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export const BankStatementsModal = ({ onClose }) => {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null); // full statement with transactions
  const [busy, setBusy] = useState(false);
  const [loadingView, setLoadingView] = useState("");
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try { setRows((await api.get("/finance/bank-statements")).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please choose a PDF bank statement");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/finance/bank-statements/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.tx_count} transactions loaded from ${file.name}`);
      await load();
      await view(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const view = async (sid) => {
    setLoadingView(sid);
    try { setActive((await api.get(`/finance/bank-statements/${sid}`)).data); setQ(""); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingView(""); }
  };

  const remove = async (sid) => {
    if (!window.confirm("Delete this statement and its parsed transactions?")) return;
    try {
      await api.delete(`/finance/bank-statements/${sid}`);
      toast.success("Statement deleted");
      if (active?.id === sid) setActive(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const txns = (active?.transactions || []).filter((t) =>
    !q.trim() || (t.description || "").toLowerCase().includes(q.toLowerCase()) || (t.date || "").includes(q));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <motion.div onClick={(e) => e.stopPropagation()} initial={{ scale: 0.97 }} animate={{ scale: 1 }}
        data-testid="bank-statements-modal"
        className="my-6 w-full max-w-5xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-display flex items-center gap-2 text-xl font-bold text-[#0F2042]"><Landmark size={18} className="text-[#F97316]" /> Bank statements</h3>
            <p className="text-xs text-slate-500">Upload any bank statement PDF — credits &amp; debits are parsed and listed automatically.</p>
          </div>
          <button onClick={onClose} data-testid="bank-statements-close"><X size={20} /></button>
        </div>

        <label className="flex cursor-pointer items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm font-bold text-slate-600 hover:border-[#F97316] hover:text-[#F97316]">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <UploadCloud size={18} />}
          {busy ? "Parsing transactions…" : "Click to upload a bank statement (PDF, max 25 MB)"}
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="bank-statement-file"
            onChange={(e) => upload(e.target.files?.[0])} disabled={busy} />
        </label>

        <div className="mt-4 grid gap-4 lg:grid-cols-[280px,1fr]">
          <div className="space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Uploaded ({rows.length})</div>
            {rows.length === 0 && <div className="rounded-xl bg-slate-50 p-4 text-xs text-slate-400" data-testid="bank-statements-empty">No statements yet.</div>}
            {rows.map((r) => (
              <div key={r.id} data-testid={`bank-statement-${r.id}`}
                className={`cursor-pointer rounded-xl border p-3 ${active?.id === r.id ? "border-[#F97316] bg-orange-50/50" : "border-slate-200 hover:border-slate-300"}`}
                onClick={() => view(r.id)}>
                <div className="truncate text-xs font-bold text-[#0F2042]">{r.filename}</div>
                <div className="mt-0.5 text-[10px] text-slate-500">{r.date_from} → {r.date_to} · {r.tx_count} txns</div>
                <div className="mt-1 flex items-center gap-2 text-[10px] font-bold">
                  <span className="text-emerald-600">+{inr(r.total_credit)}</span>
                  <span className="text-rose-600">−{inr(r.total_debit)}</span>
                  {loadingView === r.id && <Loader2 size={10} className="animate-spin" />}
                  <button onClick={(e) => { e.stopPropagation(); remove(r.id); }} data-testid={`bank-statement-del-${r.id}`}
                    className="ml-auto rounded border border-rose-200 p-1 text-rose-400 hover:bg-rose-50"><Trash2 size={10} /></button>
                </div>
              </div>
            ))}
          </div>
          <div data-testid="bank-statement-detail">
            {!active ? (
              <div className="grid h-full min-h-[220px] place-items-center rounded-2xl bg-slate-50 text-sm text-slate-400">
                Upload or select a statement to see every credit &amp; debit.
              </div>
            ) : (
              <StatementDetail active={active} txns={txns} q={q} setQ={setQ} />
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

function StatementDetail({ active, txns, q, setQ }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-emerald-50 p-3 ring-1 ring-emerald-200">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700"><ArrowDownCircle size={11} /> Total credits</div>
          <div className="mt-0.5 text-lg font-bold text-emerald-700" data-testid="stmt-total-credit">{inr(active.total_credit)}</div>
        </div>
        <div className="rounded-xl bg-rose-50 p-3 ring-1 ring-rose-200">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700"><ArrowUpCircle size={11} /> Total debits</div>
          <div className="mt-0.5 text-lg font-bold text-rose-700" data-testid="stmt-total-debit">{inr(active.total_debit)}</div>
        </div>
        <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Net</div>
          <div className={`mt-0.5 text-lg font-bold ${active.total_credit - active.total_debit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
            {inr(active.total_credit - active.total_debit)}
          </div>
        </div>
      </div>
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description or date…"
          data-testid="stmt-search" className="w-full rounded-full border border-slate-200 py-2 pl-8 pr-3 text-xs" />
      </div>
      <div className="max-h-[420px] overflow-auto rounded-2xl border border-slate-200">
        <table className="w-full text-xs" data-testid="stmt-tx-table">
          <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Debit</th>
              <th className="px-3 py-2 text-right">Credit</th>
              <th className="px-3 py-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {txns.map((t, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`stmt-tx-${i}`}>
                <td className="whitespace-nowrap px-3 py-1.5">{t.date}</td>
                <td className="max-w-[280px] truncate px-3 py-1.5" title={t.description}>{t.description || "—"}</td>
                <td className="px-3 py-1.5 text-right font-semibold text-rose-600">{t.debit ? inr(t.debit) : ""}</td>
                <td className="px-3 py-1.5 text-right font-semibold text-emerald-600">{t.credit ? inr(t.credit) : ""}</td>
                <td className="px-3 py-1.5 text-right text-slate-500">{t.balance != null ? inr(t.balance) : ""}</td>
              </tr>
            ))}
            {txns.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-400">No transactions match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

